// ============================================================================
// The subcontractor COI / W-9 vocabulary, in one place.
//
// `v_provider_compliance_status` derives insurance and W-9 state from
// `partner_documents` — the only store anyone maintains — and publishes it as
// NAMED STATES. Every view that shows a sub's compliance now republishes those
// same words (`coi_status` / `w9_status` on `v_subcontractor_scorecard`,
// `v_partner_onboarding_card`, `v_field_service_providers`).
//
// ⛔ NEVER read one of these as a boolean. The retired
// `service_providers.coi_status` mirror held the WORDS 'Yes' / 'No', and `'No'`
// is a non-empty string — so `!!row.coi_status` was true for all 43 Active subs
// recorded as uninsured, and the directory drew a green check on every one of
// them. Same defect class as `qbo_synced` TEXT 'Y'/'N' (TCSD-0064). Compare to
// a state through the helpers below; that is what they exist for.
//
// An unrecognised or null state is treated as NOT covered by every helper here,
// so a new word added upstream fails visibly rather than silently passing.
// ============================================================================

export const COI_STATES = [
  'No COI on file',
  'Expired',
  'Expires within 7 days',
  'Expires within 30 days',
  'Current',
] as const;
export type CoiState = (typeof COI_STATES)[number];

export const W9_STATES = ['No W9 on file', 'On file'] as const;
export type W9State = (typeof W9_STATES)[number];

/** A certificate exists and has not lapsed. Expiring soon is still covered — that is a chase, not a gap. */
export function coiCovered(state: string | null | undefined): boolean {
  return (
    state === 'Current' ||
    state === 'Expires within 30 days' ||
    state === 'Expires within 7 days'
  );
}

/** No valid certificate: either none was ever uploaded, or the one on file has lapsed. */
export function coiMissingOrLapsed(state: string | null | undefined): boolean {
  return state === 'No COI on file' || state === 'Expired';
}

/** A W-9 document exists. */
export function w9OnFile(state: string | null | undefined): boolean {
  return state === 'On file';
}

// Traffic-light tone for a named state. Green / amber / red, the same vocabulary
// hr-onboarding's COI_LIGHT_CLASS uses, so the dots stay consistent across the app.
export type CoiTone = 'green' | 'amber' | 'red';

export function coiStateTone(state: string | null | undefined): CoiTone {
  if (state === 'Current') return 'green';
  if (state === 'Expires within 30 days') return 'amber';
  return 'red'; // Expires within 7 days, Expired, No COI on file, unknown
}
